import { useRef, useCallback, type ReactNode } from 'react'
// Windows-style title bar glyphs - no Lucide icons needed
import { isDesktop } from '@/lib/platform'
import type { WindowState } from './WindowManager'

interface VirtualWindowProps {
  window: WindowState
  onFocus: () => void
  onClose: () => void
  onMinimize: () => void
  onMaximize: () => void
  onMove: (pos: { x: number; y: number }) => void
  onResize: (size: { w: number; h: number }) => void
  containerSize: { w: number; h: number }
  children: ReactNode
}

const MIN_WIDTH = 400
const MIN_HEIGHT = 300

export function VirtualWindow({
  window: win,
  onFocus,
  onClose,
  onMinimize,
  onMaximize,
  onMove,
  onResize,
  containerSize,
  children,
}: VirtualWindowProps) {
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null)
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null)
  const desktop = isDesktop()

  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onFocus()
      resizeRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startW: win.size.w,
        startH: win.size.h,
      }
      const el = e.currentTarget as HTMLElement
      el.setPointerCapture(e.pointerId)
    },
    [win.size, onFocus],
  )

  const handleResizeMove = useCallback(
    (e: React.PointerEvent) => {
      if (!resizeRef.current) return
      const { startX, startY, startW, startH } = resizeRef.current
      const newW = Math.max(MIN_WIDTH, startW + (e.clientX - startX))
      const newH = Math.max(MIN_HEIGHT, startH + (e.clientY - startY))
      onResize({ w: newW, h: newH })
    },
    [onResize],
  )

  const handleResizeEnd = useCallback(() => {
    resizeRef.current = null
  }, [])

  const handleDragStart = useCallback(
    (e: React.PointerEvent) => {
      if (win.maximized) return
      // Don't start drag if clicking a button in the title bar
      const target = e.target as HTMLElement
      if (target.closest('button')) return
      e.preventDefault()
      onFocus()
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: win.position.x,
        origY: win.position.y,
      }
      const el = e.currentTarget as HTMLElement
      el.setPointerCapture(e.pointerId)
    },
    [win.maximized, win.position, onFocus],
  )

  const handleDragMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return
      const { startX, startY, origX, origY } = dragRef.current
      const w = win.maximized ? containerSize.w : win.size.w
      const h = win.maximized ? containerSize.h : win.size.h
      const newX = Math.max(0, Math.min(containerSize.w - w, origX + (e.clientX - startX)))
      const newY = Math.max(0, Math.min(containerSize.h - h, origY + (e.clientY - startY)))
      onMove({ x: newX, y: newY })
    },
    [onMove, containerSize, win.maximized, win.size],
  )

  const handleDragEnd = useCallback(() => {
    dragRef.current = null
  }, [])

  if (win.minimized) return null

  const width = win.maximized ? containerSize.w : win.size.w
  const height = win.maximized ? containerSize.h : win.size.h

  return (
    <div
      className="absolute flex flex-col rounded-lg border border-border bg-card shadow-xl overflow-hidden"
      style={{
        left: win.position.x,
        top: win.position.y,
        width,
        height,
        zIndex: win.zIndex,
      }}
      onPointerDown={onFocus}
    >
      {/* Title bar - drag handle */}
      <div
        className="flex h-8 shrink-0 cursor-grab items-center justify-between border-b border-border bg-muted/50 px-3 active:cursor-grabbing select-none"
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
      >
        <span className="truncate text-xs font-medium text-foreground">{win.title}</span>
        <div className="flex items-center gap-1">
          {/* Windows-style title bar buttons */}
          {!desktop && (
            <button
              onClick={(e) => { e.stopPropagation(); onMinimize() }}
              className="flex h-6 w-8 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-xs leading-none"
              title="Minimize"
            >
              &#x2500;
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onMaximize() }}
            className="flex h-6 w-8 items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors text-xs leading-none"
            title={win.maximized ? 'Restore' : 'Maximize'}
          >
            {win.maximized ? '\u29C9' : '\u25A1'}
          </button>
          {!win.pinned && (
            <button
              onClick={(e) => { e.stopPropagation(); onClose() }}
              className="flex h-6 w-8 items-center justify-center text-muted-foreground hover:bg-red-500 hover:text-white transition-colors text-xs leading-none"
              title="Close"
            >
              &#x2715;
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>

      {/* Resize handle (bottom-right corner) */}
      {!win.maximized && (
        <div
          className="absolute bottom-0 right-0 h-3 w-3 cursor-nwse-resize"
          onPointerDown={handleResizeStart}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeEnd}
          onPointerCancel={handleResizeEnd}
        />
      )}
    </div>
  )
}
